import { useState } from "react";

export type UsePaginationOptions = {
  initialPage?: number;
  initialLimit?: number;
};

export type UsePaginationReturn = {
  page: number;
  limit: number;
  setPage: (page: number) => void;
  resetPage: () => void;
  goNext: (totalPages: number) => void;
  goPrev: () => void;
};

/**
 * Encapsulates page + limit state and navigation helpers.
 * Call resetPage() whenever filters/search change so the list goes back to page 1.
 *
 * Usage:
 *   const { page, limit, setPage, resetPage, goNext, goPrev } = usePagination();
 */
export function usePagination({
  initialPage = 1,
  initialLimit = 10,
}: UsePaginationOptions = {}): UsePaginationReturn {
  const [page, setPage] = useState(initialPage);
  const limit = initialLimit; // limit is fixed per page; expose if you need dynamic later

  const resetPage = () => setPage(initialPage);
  const goNext = (totalPages: number) =>
    setPage((p) => Math.min(totalPages, p + 1));
  const goPrev = () => setPage((p) => Math.max(initialPage, p - 1));

  return { page, limit, setPage, resetPage, goNext, goPrev };
}
